import { useState, useCallback } from 'react';
import { portalTranslations, normalizeClientLanguage } from '../i18n/portalTranslations';

const STORAGE_KEY = 'portal_language';
const SUPPORTED = ['en', 'ru'];

// Module-level locale so date helpers outside components stay in sync.
let currentLocale = 'en-US';
let currentLang = 'en';
export const getPortalDateLocale = () => currentLocale;

// Translate a status-badge label ('approved', 'In progress', ...) using the
// active portal language; unknown labels are shown as-is.
const STATUS_KEYS = {
  approved: 'approved', declined: 'declined', pending: 'pending',
  missing: 'missing', uploaded: 'uploaded', active: 'active',
  new: 'new', inprogress: 'inProgress',
};
export const translatePortalStatus = (label) => {
  const key = STATUS_KEYS[String(label || '').trim().toLowerCase().replace(/\s+/g, '')];
  return (key && portalTranslations[currentLang].status[key]) || label;
};

const readStored = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(stored) ? stored : null;
  } catch {
    return null;
  }
};

const applyLocale = (lang) => {
  currentLocale = lang === 'ru' ? 'ru-RU' : 'en-US';
  currentLang = lang;
};

const lookup = (dict, key) => {
  let value = dict;
  for (const k of key.split('.')) value = value?.[k];
  return typeof value === 'string' ? value : null;
};

// Module-level translate for helper components defined outside the hook's
// component tree; they re-render with their parent, so this stays in sync.
export const portalT = (key, params) => {
  let text = lookup(portalTranslations[currentLang], key)
    ?? lookup(portalTranslations.en, key)
    ?? key;
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      text = text.replace(`{${name}}`, value);
    }
  }
  return text;
};

// Portal-only language hook. Deliberately not the CRM LanguageContext:
// portal auth is separate, so the choice lives in localStorage and the
// default is guessed from the CRM client.language field.
export function usePortalLanguage() {
  const [language, setLanguage] = useState(() => {
    const initial = readStored() || 'en';
    applyLocale(initial);
    return initial;
  });

  const t = useCallback((key, params) => {
    let text = lookup(portalTranslations[language], key)
      ?? lookup(portalTranslations.en, key)
      ?? key;
    if (params) {
      for (const [name, value] of Object.entries(params)) {
        text = text.replace(`{${name}}`, value);
      }
    }
    return text;
  }, [language]);

  const changeLanguage = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    applyLocale(next);
    setLanguage(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const toggleLanguage = useCallback(() => {
    changeLanguage(language === 'en' ? 'ru' : 'en');
  }, [language, changeLanguage]);

  // Default from the client's CRM language, only until the user chooses.
  const applyClientDefault = useCallback((clientLanguage) => {
    if (readStored()) return;
    const guess = normalizeClientLanguage(clientLanguage);
    if (guess) {
      applyLocale(guess);
      setLanguage(guess);
    }
  }, []);

  return { language, t, toggleLanguage, changeLanguage, applyClientDefault };
}
